import React from 'react';
import { FormikErrors } from 'formik';
import { FormField } from 'common/ui';
import { RulesSettings } from 'modules/analyse/components/RulesSettings';
import { RequirementsSettings } from 'modules/analyse/components/RequirementsSettings';
import { ActionsSettings } from 'modules/analyse/components/ActionsSettings';
import { AnalyseTemplatesDropdown } from 'modules/analyse-templates/components/AnalyseTemplatesDropdown';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { AnalysisRequirementInputWithExtras } from 'modules/analyse/types/AnalysisRequirementInputWithExtras';
import { SxProps } from '@mui/material';

type Props = {
  titleSx?: SxProps;
  isVerticalForm?: boolean;
  formik: any;
  actionsEnableReinitialize?: boolean;
  include: {
    requirements?: boolean;
    rules?: boolean;
    actions?: boolean;
    templates?: boolean;
  };
  isEditable?: boolean;
  gap?: string;
  hideTemplateCreateButton?: boolean;
  autofillDefaultTemplate?: boolean;
  templateId?: string;
  onTemplateChange?: (templateId: string) => void;
};

export const AutomationComponents: React.FC<Props> = ({
  isVerticalForm = false,
  titleSx,
  formik,
  actionsEnableReinitialize = false,
  hideTemplateCreateButton = false,
  templateId,
  onTemplateChange,
  autofillDefaultTemplate = false,
  include,
  gap = '40px'
}) => {
  const baseProps = {
    isVertical: isVerticalForm,
    width: isVerticalForm ? '100%' : '60%',
    gap: isVerticalForm ? gap : undefined
  };

  return (
    <>
      {include.requirements && (
        <FormField
          key="requirements"
          {...baseProps}
          title="Requirements"
          subTitle="Add as many as you like. Adjust the weighting to reflect the needs of the job. These can be refined when an analysis is live"
          customComponents={[
            <RequirementsSettings
              key="requirements-settings"
              setFieldValue={formik.setFieldValue}
              setFieldError={formik.setFieldError}
              value={formik.values.analysisRequirements}
              errors={
                formik.errors.analysisRequirements
                  ? (formik.errors
                      .analysisRequirements as FormikErrors<AnalysisRequirementInputWithExtras>[])
                  : undefined
              }
            />
          ]}
        />
      )}

      {include.templates && (
        <FormField
          key="keys"
          {...baseProps}
          title="Templates"
          subTitle="Choose an existing template or create new rules and actions"
          customComponents={[
            <AnalyseTemplatesDropdown
              hideCreateButton={hideTemplateCreateButton}
              autofillDefaultTemplate={
                autofillDefaultTemplate &&
                formik.values.ruleAutomationTasks.length === 0 &&
                formik.values.actionAutomationTasks.length === 0
              }
              value={templateId}
              onChange={(value) => {
                formik.setFieldValue('ruleAutomationTasks', value.rules);
                formik.setFieldValue('actionAutomationTasks', value.actions);
                onTemplateChange?.(value.templateId);
              }}
            />
          ]}
        />
      )}

      {include.rules && (
        <FormField
          key="rules"
          {...baseProps}
          titleSx={titleSx}
          title="Rules"
          subTitle="Automatically apply or change a applicant's status depending on their performance"
          customComponents={[
            <RulesSettings
              key="rules-settings"
              onFormChange={(values) => {
                formik.setFieldValue('ruleAutomationTasks', values);
              }}
              analysisAutomationItems={formik.values.ruleAutomationTasks}
            />
          ]}
        />
      )}

      {include.actions && (
        <FormField
          key="actions"
          {...baseProps}
          titleSx={titleSx}
          title="Actions"
          subTitle="Depending on an applicant's status, automatically update your ATS or send them live campaigns."
          customComponents={[
            <ActionsSettings
              key="actions-settings"
              onFormChange={async (values) => {
                await formik.setFieldValue('actionAutomationTasks', values);
                await formik.validateForm();
              }}
              enableReinitialize={actionsEnableReinitialize}
              analysisAutomationItems={formik.values.actionAutomationTasks}
              errors={
                formik.errors.actionAutomationTasks
                  ? (formik.errors
                      .actionAutomationTasks as FormikErrors<AutomationFormItem>[])
                  : undefined
              }
            />
          ]}
        />
      )}
    </>
  );
};
